import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth, portalPathFor } from '../lib/auth'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

export default function Login() {
  const { user, loading, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (!loading && user) return <Navigate to={location.state?.from || portalPathFor(user.role)} replace />

  const onField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setError('')
  }

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    if (!form.email.trim() || !form.password) {
      setError('Please enter your email and password.')
      return
    }
    setBusy(true)
    try {
      const me = await signIn(form.email.trim(), form.password)
      navigate(location.state?.from || portalPathFor(me.role), { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="section" style={{ background: 'var(--color-panel)' }}>
        <div className="container-site">
          <p className="eyebrow" style={{ margin: 0 }}>Portal login</p>
          <h1 className="h1-hero" style={{ margin: '16px 0 0 0', maxWidth: 768 }}>
            Sign in to your True North portal.
          </h1>
          <p style={{ margin: '24px 0 0 0', maxWidth: 672, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
            Families, caregivers and our office team each have their own view. If you haven't received your login yet,
            call {PHONE_DISPLAY} and we'll set you up.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <form
            onSubmit={submit}
            style={{
              maxWidth: 520,
              borderRadius: 8,
              border: '1px solid var(--color-line)',
              background: '#FFFFFF',
              padding: 'clamp(24px, 5vw, 40px)',
              boxShadow: 'var(--shadow-soft)',
            }}
          >
            <h2 className="h2-section" style={{ fontSize: 'clamp(1.5rem, 4.5vw, 1.9rem)', marginBottom: 24 }}>
              Sign in
            </h2>

            <div style={{ marginBottom: 20 }}>
              <label className="field-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="field-input"
                name="email"
                type="email"
                autoComplete="username"
                value={form.email}
                onChange={onField}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="field-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="field-input"
                name="password"
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={onField}
              />
            </div>

            {error && (
              <p className="field-error" role="alert" style={{ marginBottom: 16 }}>
                {error}
              </p>
            )}

            <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%' }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <p style={{ margin: '20px 0 0 0', fontSize: 15, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              Forgotten your password? Call the office on{' '}
              <a href={PHONE_TEL} style={{ color: 'var(--color-teal-800)', fontWeight: 600 }}>
                {PHONE_DISPLAY}
              </a>{' '}
              and we'll send you a new setup link.
            </p>
          </form>

          <p style={{ margin: '28px 0 0 0', maxWidth: 520, fontSize: 15, lineHeight: 1.6, color: 'var(--color-muted)' }}>
            Portal access is set up during onboarding. If you're a True North client or caregiver without a login,
            please <Link to="/contact" style={{ color: 'var(--color-teal-800)', fontWeight: 600 }}>contact our office</Link>.
          </p>
        </div>
      </section>
    </>
  )
}
